import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, FileText, Plus, Trash2 } from "lucide-react";
import { deleteSocialReport, getMySocialReports, useAction, useQuery } from "wasp/client/operations";
import type { SocialReport } from "wasp/entities";
import { Button } from "../../client/components/ui/button";
import { useLanguageContext } from "../../client/contexts/LanguageContext";
import useTranslation from "../../client/hooks/useTranslation";
import type { ReportMetrics } from "../../social-reports/metrics/types";
import { useLiteData } from "../data/LiteDataProvider";
import { PageBody, PageHeader } from "../shell/LiteLayout";
import { Card, cardCls, Empty } from "../ui/ui";
import { GenerateReportDrawer } from "./reports/GenerateReportDrawer";
import { hasPropFirmPart, reportMoney, roiText } from "./reports/reportFormat";
import { asReviewPeriod, periodLabel, utcCalendarDate } from "./reports/reportPeriod";

/** Rapports du Lite : liste des bilans générés, une ligne par rapport, et le tiroir de génération. */
export default function LiteReportsPage() {
  const tl = useTranslation().lite;
  const t = tl.reports;
  const { data, isLoading, refetch } = useQuery(getMySocialReports);
  const del = useAction(deleteSocialReport);
  const [open, setOpen] = useState(false);

  const remove = async (id: string) => {
    if (!window.confirm(t.list.deleteConfirm)) return;
    await del({ id });
    refetch();
  };

  const reports = data ?? [];

  return (
    <>
      <PageHeader
        title={t.title}
        subtitle={t.subtitle}
        filters={false}
        actions={<Button onClick={() => setOpen(true)} aria-label={t.generate}><Plus className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">{t.generate}</span></Button>}
      />
      <PageBody>
        {isLoading ? (
          <div className={cardCls}><Empty title={tl.common.loading} /></div>
        ) : !reports.length ? (
          <section className={`${cardCls} px-6 py-14 md:py-20 text-center`}>
            <span className="w-12 h-12 rounded-2xl bg-primary/15 text-primary grid place-items-center mx-auto"><FileText size={22} /></span>
            <h2 className="font-display text-xl font-bold mt-4">{t.empty.title}</h2>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">{t.empty.text}</p>
            <div className="mt-6"><Button onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1.5" />{t.empty.cta}</Button></div>
          </section>
        ) : (
          <Card pad={false}>
            <ul className="divide-y divide-border">
              {reports.map(r => <ReportRow key={r.id} report={r} onDelete={() => remove(r.id)} />)}
            </ul>
          </Card>
        )}
      </PageBody>
      <GenerateReportDrawer open={open} onClose={() => setOpen(false)} onGenerated={refetch} />
    </>
  );
}

function ReportRow({ report, onDelete }: { report: SocialReport; onDelete: () => void }) {
  const t = useTranslation().lite.reports;
  const { language } = useLanguageContext();
  const { fmt } = useLiteData();
  const m = report.metricsSnapshot as unknown as ReportMetrics;
  const money = reportMoney(fmt, m.currency ?? "USD");

  const period = asReviewPeriod(report.period);
  const title = period
    ? periodLabel(period, utcCalendarDate(report.periodStart), language)
    : `${fmt.dateShort(report.periodStart)} – ${fmt.dateShort(report.periodEnd)}`;
  const finance = hasPropFirmPart(m) ? m.propFirmFinance ?? null : null;
  const net = m.pnlNet ?? 0;

  return (
    <li className="flex items-center gap-2 pr-2 hover:bg-muted/60 transition-colors">
      <Link to={`/reports/${report.id}`} className="flex-1 min-w-0 flex items-center gap-4 px-5 py-3.5">
        <span className="w-9 h-9 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0"><FileText size={16} /></span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold truncate">{title}</span>
          <span className="block text-xs text-muted-foreground truncate">
            {(m.scopeLabel ?? t.list.scopeAll)} · {t.list.generatedOn(fmt.dateShort(report.createdAt))}
          </span>
        </span>
        <span className="hidden sm:flex items-stretch shrink-0">
          <FigureGroup label={t.detail.trading}>
            <Figure label={t.list.net} value={money(net)} tone={net > 0 ? "gain" : net < 0 ? "loss" : undefined} />
            <Figure label={t.list.trades} value={String(m.tradesCount ?? 0)} />
          </FigureGroup>
          <FigureGroup label={t.detail.propFirm} className="hidden lg:flex border-l border-border ml-6 pl-6">
            {finance ? (
              <>
                <Figure label={t.detail.finance.spent} value={money(-finance.spent, { sign: false })} tone={finance.spent ? "loss" : undefined} />
                <Figure label={t.list.payouts} value={money(finance.payouts)} tone={finance.payouts ? "gain" : undefined} />
                <Figure label={t.list.roi} value={roiText(finance.roi, fmt)} tone={finance.roi === null ? undefined : finance.roi >= 0 ? "gain" : "loss"} />
              </>
            ) : (
              <>
                <Figure label={t.detail.finance.spent} value="-" muted />
                <Figure label={t.list.payouts} value="-" muted />
                <Figure label={t.list.roi} value="-" muted />
              </>
            )}
          </FigureGroup>
        </span>
        <ChevronRight size={16} className="text-muted-foreground/60 shrink-0" />
      </Link>
      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0" onClick={onDelete} aria-label={t.list.delete}>
        <Trash2 size={14} className="text-muted-foreground" />
      </Button>
    </li>
  );
}

/** Bloc de chiffres d'une ligne (Trading, Prop firms), titré pour qu'on lise d'un coup d'œil de quelle partie il vient. */
function FigureGroup({ label, className = "flex", children }: { label: string; className?: string; children: ReactNode }) {
  return (
    <span className={`${className} flex-col justify-center gap-1`}>
      <span className="block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 text-right">{label}</span>
      <span className="flex gap-6 text-right">{children}</span>
    </span>
  );
}

function Figure({ label, value, tone, muted }: { label: string; value: string; tone?: "gain" | "loss"; muted?: boolean }) {
  const cls = tone === "gain" ? "text-pnlPositive" : tone === "loss" ? "text-pnlNegative" : muted ? "text-muted-foreground/60" : "text-foreground";
  return (
    <span className="min-w-[72px]">
      <span className="block text-[11px] text-muted-foreground">{label}</span>
      <span className={`block font-mono tabular-nums text-sm font-semibold ${cls}`}>{value}</span>
    </span>
  );
}
